import APIs from '@/api'
import { apiClient } from '@/api/client'

// Backend envelope: { status: { success, status_code, message }, data: ... }.
// When HTTP succeeds but status.success === false, reject so react-query's
// mutation `onError` fires (and Swal can show the backend message).
function assertSuccess(res, fallbackMessage = 'Request failed') {
  const ok = res?.data?.status?.success
  if (ok === false) {
    const message = res?.data?.status?.message || res?.data?.message || fallbackMessage
    const err = new Error(message)
    err.response = res
    throw err
  }
  return res
}

// GET /branches?limit&page&search&status
// PaginationFooter sends params object; map to backend shape.
// Backend envelope: { status, data: { data: [...], meta: { total, ... } } }.
// PaginationFooter's fallback chain reads `response.data.data` expecting an array,
// so we unwrap one level and return a normalized envelope.
export const listBranchesApi = async (params = {}) => {
  const {
    per_page,
    current_page,
    search,
    filterFields,
    filterInputs,
    sort_column,
    sort_direction,
  } = params || {}

  const query = { limit: per_page, page: current_page }
  if (search) query.name = search
  if (Array.isArray(filterFields) && Array.isArray(filterInputs)) {
    filterFields.forEach((field, idx) => {
      const value = filterInputs[idx]
      if (value === undefined || value === null || value === '') return
      if (field === 'status' && value === 'all') return
      query[field] = value
    })
  }
  if (sort_column) query.sortBy = sort_column
  if (sort_direction) query.direction = sort_direction

  const res = await apiClient.get(APIs.BRANCHES.list, { params: query })
  const inner = res?.data?.data
  const list = Array.isArray(inner?.data)
    ? inner.data
    : Array.isArray(inner)
      ? inner
      : Array.isArray(res?.data)
        ? res.data
        : []
  const meta = inner?.meta || res?.data?.meta || {}
  const total = meta.total ?? res?.data?.total ?? list.length

  return { ...res, data: { data: list, meta, total } }
}

// Wrapper for CustomAutoSelect — returns { data: [...], meta: { total, totalPages } }
export const listBranchesForSelect = async ({ page, limit, search }) => {
  const params = { page, limit }
  if (search) params.search = search
  const res = await apiClient.get(APIs.BRANCHES.list, { params })
  const list = res?.data?.data || res?.data || []
  const total = res?.data?.total ?? res?.meta?.total ?? list.length
  const totalPages = res?.data?.meta?.totalPages ?? Math.ceil(total / limit)
  return { data: list, meta: { total, totalPages } }
}

export const getBranchApi = async (uuid) =>
  assertSuccess(await apiClient.get(`${APIs.BRANCHES.detail}/${uuid}`))

export const createBranchApi = async (payload) =>
  assertSuccess(await apiClient.post(APIs.BRANCHES.create, payload), 'Failed to create branch')

export const updateBranchApi = async (uuid, payload) =>
  assertSuccess(
    await apiClient.put(`${APIs.BRANCHES.update}/${uuid}`, payload),
    'Failed to update branch'
  )

export const updateBranchStatusApi = async (uuid, status) =>
  assertSuccess(
    await apiClient.put(`${APIs.BRANCHES.status}/${uuid}/status`, { status }),
    'Failed to update status'
  )

export const deleteBranchApi = async (uuid) =>
  assertSuccess(
    await apiClient.delete(`${APIs.BRANCHES.remove}/${uuid}`),
    'Failed to delete branch'
  )
